import { Div, onClick, klass } from "./html"
import {useState,useRef, useEffect, useCallback} from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { Identifier, XYCoord } from 'dnd-core'
import {Device,SubModule,Chain,Channel,ModuleDef,IChain,TModuleRole,IDevice,IDeviceDef, IModuleDef, DeviceDef, TRecDeviceDef, TRecModuleDef, TPhonia, ModuleRole, Phonia, TRecChain, Names} from './model'
import {EditableTextBox,NodeIcon,CTAButton,Tip } from './widgets'
import { Tags } from "./tag"
import { PopupMenu, IMenuChoice, TriPop } from "./menu"
import { Identity } from "./models/id"
import { ITagController } from "./models/tag"
import { Events } from "./event"
import { SelectScroll } from "./scroll"
import { IArch, ChannelPort } from './arch'

let module: never

const L = (...args) => window.console.log(...args)
const D = Div

// DEEP COPY
function clone<T>(object:T) : T {
    if (object == null) return object
    return JSON.parse(JSON.stringify(object)) as T
}

// Get ordered node IDs for a device (falls back to Object.keys if no order set)
function getNodeOrder(device: IDeviceDef): string[] {
    return device.nodeOrder ?? Object.keys(device.nodes)
}

export function BrowserDevice (
    {
        device,
        selected,
        onChoose,
        onDelete,
    }:
    {
        device: IDevice
        selected?: boolean
        onChoose: () => void
        // cmd+click DELETE gesture (guard/confirm handled by caller); presence also lights the affordance
        onDelete?: () => void
    })
{
    const {name} = device
    const ref = useRef<HTMLDivElement>(null)
    // SCROLL into VIEW if selected — muted when a tab switch just remounted the list
    useEffect(() => {
        if (SelectScroll.muted()) return
        selected && ref.current?.scrollIntoView({ behavior: 'smooth' })
    }, [selected])
    return (
        <div
            ref={ref}
            className={klass('browser-device',selected && 'selected',onDelete && 'deletable')}
            // PREEMPT the onClick select below — capture-phase stopPropagation skips the bubble handler.
            // Mirrors the chain module/header cmd+delete gesture (module.tsx ChainModule).
            onClickCapture={e => {
                if (e.button === 0 && Events.cmd(e) && onDelete) {
                    onDelete()
                    e.stopPropagation()
                    e.preventDefault()
                }
            }}
            onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                onChoose()
            }}
        >
            <D k={['name']}>{name}</D>
        </div>
    )
}

/**
 * Module as would appear in device config
 */
export function DeviceModule ({
    name,device,
    proc,dest,source,
    stereo,m2s,
} : {
    name: string
    device: string
    proc?: boolean
    dest?: boolean
    source?: boolean
    stereo?: boolean
    m2s?: boolean
})
{
    // spans + metadata
    const $ = {
        device: { k: 'device', child: device },
        name: { k: 'name', child: name },
        channels: 
                stereo ? <>
                    <Div k={['channel','stereo','L']}>L</Div>
                    <Div k={['channel','stereo','R']}>R</Div>
                </>
                : m2s ? <><Div k={['channel','m2s']}>M2S</Div></>
                : <><Div k={['channel']}>M</Div></>
    }

    return (
        <Div 
            k={[
                'chain-module',
                stereo && 'stereo',
                proc ? 'proc' : source ? 'source' : dest ? 'dest' : 'none',
            ]}>
                <D k='frame'>
                    <Div {...$.device}/>
                    <Div {...$.name}/>
                    {$.channels}
                </D>
        </Div>
    )
}

/**
 * Modally prompt required values for device creation.
 * If canceled, value is `undefined`.
 */
function NewDevPrompt (
    {
        control,
        onClose,
    }: 
    {
        control: {
            all: () => TRecDeviceDef
        },
        onClose: (args?: {
            prod?: string
            mfr?: string
            name: string
        }) => void
    }
)
{
    type $State = {
        prod?: string
        mfr?: string
        name?: string
        defName?: string
    }

    const ref = useRef(null)
    const state = useRef<$State>({})
    const activeField = useRef<number>(-1)
    const [prod,setProd] = useState<string>()
    const [mfr,setMfr] = useState<string>()
    const [name,setName] = useState<string>()
    const [defName,setDefName] = useState<string>()

    const [,setActiveField] = useState<number|null>()
    // visual order: CANCEL left, OK right (unified dialog spec)
    const fields = [ 'mfr', 'prod', 'name', 'cancel', 'ok' ]

    state.current = {
        prod,
        mfr,
        name,
        defName,
    }

    // GENERATE and UPDATE "default" display name
    function updateDefName (mfr?:string,prod?:string) : string | undefined {
        const name = DeviceDef.getUniqueName({mfr,prod,devices:control.all()})
        setDefName(name)
        state.current.defName = name
        return name
    }

    useEffect(() => {
        const onKey = (e:KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                e.preventDefault()
                onClose()
            }
        }
        // INSTALL KEYHANDLER on MOUNT
        document.addEventListener('keydown', onKey)
        // REMOVE on UNMOUNT
        return () => document.removeEventListener('keydown', onKey)
    }, [])

    const isValid = () => {
        const {mfr,prod,name,defName} = state.current
        return !!(prod || mfr || name || defName)
    }

    /** ACTIVE first field on load  */
    if (activeField.current === -1) {
        setTimeout(() =>  {
            activeField.current = 0
            setActiveField(0)
        },0)
    }

    // TAB key
    useEffect(() => {
        const onTab = (e: KeyboardEvent) => {
            // ACTIVATE next field/control
            if (e.key === 'Tab') {
                if (Events.plain(e)) {
                    const field = activeField.current
                    let nextField =
                        field < 0
                        ? 0
                        : (field + 1) < fields.length
                        ? field + 1
                        : -1
                    if (fields[nextField] === 'ok' && !isValid()) {
                        // 'ok' is the last field: skipping it wraps around
                        nextField = (nextField + 1) < fields.length ? nextField + 1 : -1
                    }
                    activeField.current = nextField
                    setActiveField(nextField)
                }
                else if (Events.shift(e)) {
                    const field = activeField.current
                    let nextField =
                        field < 0
                        ? fields.length - 1
                        : field === 0
                        ? -1
                        : field - 1
                    if (fields[nextField] === 'ok' && !isValid()) {
                        nextField--
                    }
                    activeField.current = nextField
                    setActiveField(nextField)
                }
                e.preventDefault()
                e.stopPropagation()
            }
        }
        document.addEventListener('keydown', onTab)
        return () => document.removeEventListener('keydown', onTab)
    }, [ref])

    const active = activeField.current == null ? null : fields[activeField.current]
    L('activeField',activeField.current,active)
    return (
        <D k='modal' s={{display:'block'}}>
            <D k='dialog newnode'>
                <D k="titlebar">CREATE DEVICE
                    <D k='dismiss' {...onClick(() => onClose())}>close</D>
                </D>
                <D k={['body']}>
                    <DeviceNameSubpanel
                        mfr={mfr}
                        prod={prod}
                        name={name ? name : defName}
                        active={{
                            mfr:  active === 'mfr',
                            prod: active === 'prod',
                            name: active === 'name',
                            onActive: name => activeField.current = fields.findIndex(_ => _ === name)
                        }}
                        control={{
                            all: () => control.all(),
                            edit: {
                                name: value => {
                                    const name = value || defName
                                    state.current.name = name
                                    return name
                                },
                                prod: value => {
                                    state.current.prod = value
                                    updateDefName(mfr,value)
                                    return value
                                },
                                mfr: value => {
                                    state.current.mfr = value
                                    updateDefName(value,prod)
                                    return value
                                },
                            },
                            modify: {
                                name: value => {
                                    setName(value)
                                    const name = value || defName
                                    state.current.name = name
                                    return name
                                },
                                prod: value => {
                                    setProd(value)
                                    state.current.prod = value
                                    return value
                                },
                                mfr: value => {
                                    setMfr(value)
                                    state.current.mfr = value
                                    return value
                                },
                            },
                        }}
                    />
                    <D k="actions">
                        <CTAButton
                                k='secondary'
                                selected={active === 'cancel'}
                                click={() => onClose()}>
                            CANCEL
                        </CTAButton>
                        <CTAButton
                                k='primary'
                                disabled={!isValid()}
                                selected={active === 'ok'}
                                click={() => {
                                    L('CLICK')
                                    const {mfr,prod,name,defName} = state.current
                                    isValid() && onClose({mfr, prod, name: name || defName!})
                                }}>
                            OK
                        </CTAButton>
                    </D>
                </D>
            </D>
        </D>
    )
}

/**
 * Modally prompt required values for module creation. Values are received via `onClose` if accepted.
 * If canceled, value is `undefined`.
 */
function NewModPrompt (
    {
        device,
        devices,
        onClose,
        setAlert,
    }: 
    {
        device: IDeviceDef
        devices: TRecDeviceDef
        onClose: (args?: {
            name: string
            role: TModuleRole
            phonia: TPhonia
        }) => void
        setAlert: (prompt:string) => void
    }
)
{
    useEffect(() => {
        const onKey = (e:KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                e.preventDefault()
                onClose()
            }
        }
        // INSTALL KEYHANDLER on MOUNT
        document.addEventListener('keydown', onKey)
        // REMOVE on UNMOUNT
        return () => document.removeEventListener('keydown', onKey)
    }, [])

    const defNodeMode: INodeMode = {phonia: {mono:true}, role: {source:true}}
    const [name,setName] = useState<string|undefined>(NodeMode.uniqueModeName(defNodeMode,device))
    const [role,setRole] = useState<TModuleRole>(defNodeMode.role)
    const [phonia,setPhonia] = useState<TPhonia>(defNodeMode.phonia)

    // INDEX current device module names
    const modNames = Object
        .values(DeviceDef.extractNodes(devices))
        // CURRENT device
        .filter(({device:{id}}) => id === device.id)
        .reduce((p,mod) => {
            const role = ModuleRole.toString(mod.role)
            p[role] = p[role] ?? {}
            p[role][mod.name] = mod.name
            return p
        },{})

    const valid = !!name?.length
        // NO NAME conflicts
        && !modNames[name]

    return (
        <div className='modal' style={{display:'block'}}>
            <D k={['dialog','newnode']}>
                <D k="titlebar">CREATE NODE
                    <D k='dismiss' {...onClick(() => onClose())}>close</D>
                </D>
                <D k={['body']}>
                    <D k='fields'>
                        <D k='field'>
                            <D k='name'>Type</D>
                            <ModeChoices 
                                    mode={{role,phonia}}
                                    modify={(role,phonia) => {
                                        setName(NodeMode.uniqueModeName({role,phonia},device))
                                        setRole(role)
                                        setPhonia(phonia)
                                    }}
                            />
                        </D>
                        <D k={['field','name']}>
                            <D k='name'>Name</D>
                            <EditableTextBox
                                k={['name','editbox']}
                                emptyText='enter node name'
                                value={name}
                                active={true}
                                onSave={value => {
                                    if (value) {
                                        const $role = ModuleRole.toString(role)
                                        // XXX DRY
                                        if (modNames[$role] && modNames[$role][value]) {
                                            setAlert('Node name must be unique for device and type')
                                        }
                                        else {
                                            setName(value)
                                            return value
                                        }
                                    }
                                    else {
                                        setAlert('Node name is required')
                                    }
                                }
                            }/>
                        </D>
                    </D>
                    <D k="actions">
                        <D k="cta secondary" {...onClick(() => onClose())}>CANCEL</D>
                        <D k={['cta','primary', !valid && 'disabled']} {
                            ...onClick(() => valid && onClose({ name, role, phonia })
                        )}>OK</D>
                    </D>
                </D>
            </D>
        </div>
    )
}

export interface IDevConfigController {
    all: () => TRecDeviceDef
    modify (device:IDeviceDef) : IDeviceDef | undefined
}

function ModPort ({ io,port,choices } :
    {
        io: 'from' | 'to', 
        port?: string,
        choices: IMenuChoice[],
    }
)
{
    const [menuVisible, setMenuVisible] = useState(false)
    const ref = useRef(null)
    return (
        <D k='port-choice'>
            {io === 'from' && <D k='arrow symbols from'>arrow_drop_down</D>}
            <D
                    className='port'
                    ref={ref} 
                    onClick={() => setMenuVisible(!menuVisible)}>
                {port ? port : io === 'to' ? 'Out (to)' : 'In (from)'}
                <PopupMenu
                    k='port-choose'
                    triggerRef={ref as React.RefObject<any>}
                    isVisible={menuVisible}
                    close={() => setMenuVisible(false)}
                    items={choices}
                />
            </D>
            {io === 'to' && <D k='arrow symbols to'>arrow_drop_down</D>}
        </D>
    )
}

interface INodeMode {
    phonia:TPhonia
    role:TModuleRole
}

class NodeMode {
    static uniqueModeName (
            self: INodeMode,
            device: IDeviceDef,
        )
    {
        // INDEX device names
        const names = Object.values(device.nodes)
            .reduce((p,node) => {
                p[node.name] = node.name
                return p
            },{})

        const name = NodeMode.toStr(self) // base name
        // UNIQUE w/o suffix
        if (!names[name]) return name

        // FIND unique suffix
        let index = 1
        while (names[`${name} ${index}`]) {
            index++
        }
        return `${name} ${index}`
    }

    static toStr (self:INodeMode) : string {
        return `${Phonia.toString(self.phonia)} ${ModuleRole.toString(self.role)}`
    }

    static toLabel (self:INodeMode) : string {
        // OVERRIDE M2S stringification
        const $phonia = Phonia.same(Phonia.M2S,self.phonia)
            ? 'Mono to Stereo'
            : Phonia.toString(self.phonia)
        return `${$phonia} ${ModuleRole.toString(self.role)}`
    }
}

function ModeChoices ({mode,modify} :
    {
        mode: INodeMode
        modify: (role:TModuleRole,phonia:TPhonia) => void
    }
)
{
    const [menuVisible, setMenuVisible] = useState(false)
    const ref = useRef(null)

    const choices : IMenuChoice[] =
        Phonia.all()
        .map(phonia =>
            ModuleRole.all()
            .filter(role => !phonia.m2s || role.proc)
            .map(role => (
                {
                    label: NodeMode.toLabel({phonia, role}),
                    k: 'module-type-choice',
                    choose: () => {
                        modify(role,phonia)
                    }
                }
            ))
        )
        .flat()

    return (
        <D
                className='modes'
                ref={ref} 
                onClick={() => setMenuVisible(!menuVisible)}>
            <NodeIcon size={12}/>
            <D k='mode'>{NodeMode.toLabel(mode)}</D>
            <D k={['down','symbols']}>keyboard_arrow_down</D>
            <PopupMenu
                triggerRef={ref as React.RefObject<any>}
                isVisible={menuVisible}
                close={() => setMenuVisible(false)}
                items={choices}
            />
        </D>
    )
}

function ModuleShape (
    {
        module,
        arch,
        ports,
        modify,
    } :
    {
        module: IModuleDef
        arch: IArch
        ports: (io: 'to'|'from',cb: (port?:string) => void) => IMenuChoice[]
        modify: (mod: IModuleDef) => void
    }
)
{
    const {role,phonia,L,R} = module

    /**
     * MODIFY channel port for given side and direction
     * @param port 
     */
    function modPort (side: 'L'|'R', dir: 'TO'|'FROM', portId?: string) {
        const pair = side === 'L' ? L : R
        // OPPOSITE channel pair
        const oppo = side === 'L' ? R : L
        // TRANSLATE port ID to numeric port
        const port = portId ? ChannelPort.fromPortId(portId,arch) : undefined

        if (dir === 'TO') {
            // IS PORTID already used by this module on opposite stereo channel?
            // RELEASE IT!
            if (oppo.to && arch.ports.isConflict(oppo.to.port,port)) {
                oppo.to = undefined
            }
            pair.to = portId ? Channel.create({ to: portId, arch }) : undefined
        }
        else {
            // IS PORTID already used by this module on opposite stereo channel?
            // RELEASE IT!
            if (oppo.from && arch.ports.isConflict(oppo.from.port,port)) {
                oppo.from = undefined
            }
            pair.from = portId ? Channel.create({ from: portId, arch }) : undefined
        }
        modify(module)
    }

    return (
        <D k='shape'>
            <D k={['high','ports']}>
                {!role.source &&
                    <ModPort
                        io='to'
                        port={L.to?.port != null && arch.ports.isValid(L.to.port) ? ChannelPort.toPortId(L.to.port,arch) : undefined}
                        choices={ports('to',(port?:string) => modPort('L','TO',port))}
                    />
                }
                {!role.source && phonia.stereo &&
                    <ModPort
                        io='to'
                        port={R.to?.port != null && arch.ports.isValid(R.to.port) ? ChannelPort.toPortId(R.to.port,arch) : undefined}
                        choices={ports('to',(port?:string) => modPort('R','TO',port))}
                    />
                }
            </D>
            <DeviceModule {...{
                stereo: phonia.stereo,
                m2s: phonia.m2s,
                name: module.name,
                device: module.device.name,
                ...module.role,
            }}/>
            <D k={['low','ports']}>
                {!role.dest &&
                    <ModPort
                        io='from'
                        port={L.from?.port != null && arch.ports.isValid(L.from.port) ? ChannelPort.toPortId(L.from.port,arch) : undefined}
                        choices={ports('from',(port?:string) => modPort('L','FROM',port))}
                    />
                }
                {!role.dest && !phonia.mono &&
                    <ModPort
                        io='from'
                        port={R.from?.port != null && arch.ports.isValid(R.from.port) ? ChannelPort.toPortId(R.from.port,arch) : undefined}
                        choices={ports('from',(port?:string) => modPort('R','FROM',port))}
                    />
                }
            </D>
        </D>
    )
}

function getPortChoices (
        arch: IArch,
        modules: TRecModuleDef,
        onChoose: (port?:string) => void,
) : {
        to: IMenuChoice[],
        from: IMenuChoice[],
}
{
    // INDEX modules by their assigned ports
    const portIndex : {
            to: Record<string,IModuleDef[]>,
            from: Record<string,IModuleDef[]>,
        } = Object
        .values(modules)
        .reduce((p,module) => {
            const {to,from} = ModuleDef.ports(module,arch)
            to.forEach(port => (p.to[port] = p.to[port] ?? []).push(module))
            from.forEach(port => (p.from[port] = p.from[port] ?? []).push(module))
            return p
        },{to:{},from:{}})

    function makeChoice (port:string,modules:IModuleDef[]) {
        // MODULES having registered this port
        return {
            k: 'portlabel',
            el: true,
            // NAME of port + first (if any) module claiming it
            label:
                <>
                    <D k='port'>{port}</D>
                    {modules.length ? <D k='mod'>{modules[0].device.name||''} {modules[0].name}</D> : false}
                </>,
            // CREATE submenu showing all other modules claiming this port
            choices: modules[1]
                ? modules
                    .map(mod => ({
                        k: 'nodename',
                        disabled: true,
                        label: `${mod.device.name||''} ${mod.name}`
                    }))
                :  undefined,
            choose: onChoose ? () => onChoose(port) : undefined
        }
    }

    function bankSection (dir: 'to' | 'from') {
        return arch.ports.banks.map(bank => {
            // Enumerate physical channels for this bank, then let the arch
            // synthesize the display ID — robust to non-contiguous FP ranges.
            const channels = bank === 'FP'
                ? arch.ports.fpActive.flatMap(r =>
                    Array.from({length: r.end - r.start + 1}, (_, i) => -(r.start + i)))
                : (() => {
                    const r = arch.ports.bp.find(b => b.bank === bank)
                    return r
                        ? Array.from({length: r.end - r.start + 1}, (_, i) => r.start + i)
                        : []
                  })()
            return {
                label: bank,
                choices: channels.flatMap(ch => {
                    const port = arch.ports.toPortId(ch)
                    if (!port) return []
                    return [makeChoice(port, portIndex[dir][port] || [])]
                })
            }
        })
    }

    return {
        to: [
            { label: 'Out (to)', divider: 'bottom' },
            { label: 'Unassign', divider: 'bottom', choose: () => onChoose() },
            ...bankSection('to'),
        ],
        from: [
            { label: 'In (from)', divider: 'bottom' },
            { label: 'Unassign', divider: 'bottom', choose: () => onChoose() },
            ...bankSection('from'),
        ]
    }
}

interface DragItem {
    index: number
    id: string
    type: string
}

function ModuleConfigPanel (
    {
        arch,
        module,
        control,
        selected,
        setAlert,
        onSelect,
        index,
        moveNode,
    } :
    {
        arch: IArch
        module: IModuleDef
        control: {
            module: {
                get () : TRecModuleDef
                modify (module: IModuleDef) : IModuleDef
                copy? () : void
                remove () : void
                tags: ITagController
            }
        }
        setAlert: (prompt:string) => void
        selected?: boolean
        onSelect: (id:string) => void
        index: number
        moveNode: (dragIndex: number, hoverIndex: number) => void
    }
)
{
    const modules = control.module.get()

    // INDEX device module names
    const modNames = Object
        .values(modules)
        // CURRENT device
        .filter(({device:{id}}) => id === module.device.id)
        // NOT current module
        .filter(({id}) => id !== module.id)
        .reduce((p,mod) => {
            const role = ModuleRole.toString(mod.role)
            p[role] = p[role] ?? {}
            p[role][mod.name] = mod.name
            return p
        },{})

    const valid = ModuleDef.valid(module,arch)

    const escapeRE = (s:string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    module = clone(module)

    // DND: sortable node behavior
    const ref = useRef<HTMLDivElement>(null)
    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: 'device-node',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            }
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) return
            const dragIndex = item.index
            const hoverIndex = index
            if (dragIndex === hoverIndex) return

            const hoverBoundingRect = ref.current.getBoundingClientRect()
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
            const clientOffset = monitor.getClientOffset()
            if (!clientOffset) return
            const hoverClientY = clientOffset.y - hoverBoundingRect.top

            // Dragging down: only move when cursor passes midpoint
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
            // Dragging up: only move when cursor passes midpoint
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

            moveNode(dragIndex, hoverIndex)
            item.index = hoverIndex
        },
    })

    const [{ isDragging }, drag] = useDrag({
        type: 'device-node',
        item: () => ({ id: module.id, index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    })

    drag(drop(ref))

    return (
        <D
            ref={ref}
            key={module.id}
            k={['module',selected && 'selected']}
            onClick={() => onSelect(module.id)}
            style={{ opacity: isDragging ? 0 : 1 }}
            data-handler-id={handlerId}
        >
            <D k='header'>
                <EditableTextBox
                    k='name'
                    editOn='dblclick'
                    value={module.name}
                    onSave={value => {
                        if (value) {
                            const $role = ModuleRole.toString(module.role)
                            if (modNames[$role] && modNames[$role][value]) {
                                setAlert('Node name must be unique for device and type')
                                return module.name
                            }
                            else {
                                module.name = value
                                control.module.modify(module)
                                return value
                            }
                        }
                        else {
                            setAlert('Node name is required')
                            return module.name
                        }
                    }}
                />
                <TriPop
                    items={[
                        control.module.copy && {
                            label:'Duplicate',
                            choose: () => control.module.copy!(),
                        },
                        control.module.remove && {
                            label:'Delete',
                            choose: () => control.module.remove!(),
                        },
                    ]}/>
            </D>
            <D k='body'>
                <D k={['port-assign','subpanel']}>
                    <ModuleShape
                        arch={arch}
                        module={module}
                        ports={(dir, cb) => {
                            const choices = getPortChoices(arch,control.module.get(),cb)
                            return dir === 'to' ? choices.to : choices.from
                        }}
                        modify={modded => control.module.modify(modded)}
                    />
                </D>
                <D k={['subpanel','status']}>
                    <D k='type-choose'>
                        <D k='header'>Type</D>
                        <ModeChoices 
                            mode={module}
                            modify={(role,phonia) => {
                                // NO MODIFICATION
                                if (ModuleRole.same(role,module.role) && Phonia.same(phonia,module.phonia)) {
                                    return
                                }
                                const $role = ModuleRole.toString(role)
                                if (modNames[$role] && modNames[$role][module.name]) {
                                    setAlert('Node name must be unique for device and type')
                                }
                                else {
                                    // CURRENT name of module "default"? RENAME to match chosen mode
                                    const defName = NodeMode.toStr(module)
                                    if (defName === module.name || (new RegExp(`${escapeRE(defName)} \\d+$`)).test(module.name)) {
                                        // NEW module "default" name - FIND unique suffix
                                        let modName = NodeMode.toStr({phonia,role})
                                        // FIND unique default name
                                        if (modNames[$role]) {
                                            let index = 0
                                            while (modNames[$role][modName]) {
                                                modName = `${modName} ${++index}`
                                            }
                                        }
                                        module.name = modName
                                    }
                                    module = control.module.modify(ModuleDef.setMode(module,role,phonia))
                                }
                            }}
                        />
                    </D>
                    <D k='port-status'>
                        <D k='subheader'>Port ID Assignment</D>
                        <D k={['completion', !valid && 'incomplete']}>
                            {valid ? 'Complete' : 'Incomplete'}
                            {!valid &&
                                <Tip k='info' text='All Port IDs need to be assigned.'>
                                    {'ⓘ'}
                                </Tip>
                            }
                        </D>
                    </D>
                </D>
                <D k={['subpanel']}>
                    <D k='header'>Tags</D>
                    <Tags 
                        tags={module.tags}
                        name={module.name}
                        control={control.module.tags}
                    />
                </D>
            </D>
        </D>
    )
}

/**
 * INDEX chains by (unsplit) module ID (that are contained therein)
 */
function indexUnsplitMods (chains: IChain[]) : Record<string,IChain[]> {
    // INDEX all chain-referenced modules
    return chains.reduce((p,chain) => {
        Chain
            .nodes(chain)
            // "UNSPLIT" and INDEX chain modules
            .forEach(mod => {
                const id = SubModule.getSuper(mod) ?? mod.id
                p[id] = true
            })
        return p
    },{})
}

/**
 * Is any node of `device` referenced by a chain? (Deletion guard — a referenced
 * device cannot be removed.) Shared by the DeviceConfig TriPop delete and the
 * cmd+click delete gesture on browser rows (upstage.tsx DeviceBrowser).
 */
export function deviceInUse (device: IDeviceDef, chains: IChain[]) : boolean {
    const used = indexUnsplitMods(chains)
    return Object.values(device.nodes).some(({id}) => used[id])
}

export interface IDeviceConfigControl {
    devices: {
        modify (device:IDeviceDef) : IDeviceDef | undefined
        remove (device:IDeviceDef)
        copy (device:IDeviceDef) : IDeviceDef | undefined
        add (device:IDeviceDef) : IDeviceDef
        all: () => TRecDeviceDef
        tags (module:IModuleDef) : ITagController
    }
    chains: {
        get: () => TRecChain
    }
}

export function DeviceConfig ({
    arch,
    device,
    devices,
    control,
    newDevModal,
    setAlert,
    setDevice,
} : {
    arch: IArch
    devices: TRecDeviceDef
    device?: IDeviceDef
    newDevModal?: {
        current: () => void
    }
    control: IDeviceConfigControl
    setAlert: (prompt:string) => void
    setDevice: (device?:string) => void
})
{
    const [newModal,setNewModal] = useState<'dev'|'mod'>()
    const [selectedMod,setSelectedMod] = useState<string>()
    const [sortAsc,setSortAsc] = useState(true)

    useEffect(() => {
        // ALLOWS outside agent to trigger new device prompt
        if (newDevModal) {
            newDevModal.current = () => setNewModal('dev')
        }
    })

    // REMOVE device
    function removeDev () {
        if (device) {
            const devices = control.devices.all()
            if (devices[device.id]) {
                if (deviceInUse(device, control.chains.get())) {
                    setAlert('Cannot delete: One or more chains reference nodes/Port IDs of this device')
                }
                else {
                    control.devices.remove(device)
                    // RESET browser device
                    setDevice(undefined)
                }
            }
        }
    }

    // COPY device
    function copyDev () {
        if (device) {
            const copy = control.devices.copy(device)
            if (copy) {
                setDevice(copy.id)
            }
        }
    }

    // Move node to new position (reorder)
    const moveNode = useCallback((dragIndex: number, hoverIndex: number) => {
        if (!device) return
        const $device = clone(device)
        const nodeIds = [...getNodeOrder($device)]
        const [moved] = nodeIds.splice(dragIndex, 1)
        nodeIds.splice(hoverIndex, 0, moved)
        $device.nodeOrder = nodeIds
        control.devices.modify($device)
    }, [device, control.devices])

    // Sort nodes by name (persisted to nodeOrder), toggling direction each click
    const sortNodes = () => {
        if (!device) return
        const $device = clone(device)
        const order = [...getNodeOrder($device)]
            .sort((a,b) => Names.sorter($device.nodes[a]?.name ?? '', $device.nodes[b]?.name ?? ''))
        if (!sortAsc) order.reverse()
        $device.nodeOrder = order
        control.devices.modify($device)
        setSortAsc(!sortAsc)
    }

    return (
        <D k={['devcfg',!device && 'empty']}>
            {
                newModal === 'mod'
                ? device && <NewModPrompt
                    device={device}
                    devices={devices}
                    setAlert={setAlert}
                    onClose={args => {
                        if (args) {
                            // LATEST device ref
                            // XXX not sure this matters
                            const $device = {...control.devices.all()[device.id]}
                            const {name,role,phonia} = args
                            const mod:IModuleDef = {
                                id: Identity.create(),
                                name,
                                L:{},
                                R:{},
                                role,
                                phonia,
                                tags: [],
                                device: Device.fromDef($device),
                            }
                            device.nodes[mod.id] = mod
                            device.nodeOrder = [...getNodeOrder(device).filter(id => id !== mod.id), mod.id]
                            L('NODE-CREATE',mod.id)
                            control.devices.modify(device)
                        }
                        setNewModal(undefined)
                    }}
                />
                : newModal === 'dev'
                && <NewDevPrompt
                    control={{
                        all: () => control.devices.all()
                    }}
                    onClose={args => {
                        if (args) {
                            const {mfr,prod,name} = args
                            const device:IDeviceDef = {
                                id: Identity.create(),
                                name,
                                product: {
                                    mfr,
                                    name: prod,
                                },
                                nodes: {},
                            }
                            const node:IModuleDef = {
                                id: Identity.create(),
                                name: 'Mono Processor',
                                device: Device.fromDef(device),
                                L:{},
                                R:{},
                                role: {proc:true},
                                phonia: {mono:true},
                                tags: [],
                            }
                            device.nodes[node.id] = node
                            const $device = control.devices.add(device)
                            if ($device) {
                                setDevice($device.id)
                            }
                        }
                        // DISABLE new device prompt (in parent)
                        setNewModal(undefined)
                    }}
                />
            }
            {
                device ?
                <>
                    <D k='header'>
                        <D k='title'>{device.name}</D>
                        <TriPop
                            items={[
                                {
                                    label:'Duplicate',
                                    choose: () => copyDev(),
                                },
                                {
                                    label:'Delete',
                                    choose: () => removeDev(),
                                },
                            ]}/>
                    </D>
                    <D k='panels'>
                        <DeviceConfigPanel
                            k='device'
                            setAlert={setAlert}
                            device={device}
                            control={{
                                all: () => control.devices.all(),
                                modify: dev => control.devices.modify(dev),
                            }}
                        />
                        <D k='modules'>
                            <D k='node-sort' onClick={sortNodes}>
                                {sortAsc ? 'A-Z' : 'Z-A'}<span className="mui-icons">{sortAsc ? 'south' : 'north'}</span>
                            </D>
                            {
                                getNodeOrder(device)
                                .map((nodeId, index) => {
                                    const module = device.nodes[nodeId]
                                    if (!module) return null
                                    return (
                                    <ModuleConfigPanel
                                        arch={arch}
                                        key={module.id}
                                        index={index}
                                        moveNode={moveNode}
                                        selected={selectedMod === module.id}
                                        onSelect={id => setSelectedMod(id)}
                                        module={module}
                                        setAlert={setAlert}
                                        control={{
                                            module: {
                                                get: () => DeviceDef.extractNodesIndexed(control.devices.all()),
                                                modify (next:IModuleDef) {
                                                    L('NODE-MODIFY',next.id)
                                                    // CURRENT state of module
                                                    // XXX use reference already available in closure?
                                                    const now = module
                                                    const chains = control.chains.get()
                                                    const used = indexUnsplitMods(chains)
                                                    // MODALITY cannot be modified while referenced by ANY CHAIN
                                                    if ((!ModuleRole.same(next.role,now.role) || !Phonia.same(next.phonia,now.phonia)) && used[module.id]) {
                                                        setAlert('Cannot modify type: One or more chains reference this node')
                                                    }
                                                    else if (ModuleDef.valid(now,arch) && !ModuleDef.valid(next,arch) && used[module.id]) {
                                                        setAlert('Cannot unassign Port ID: One or more chains reference this node')
                                                    }
                                                    else {
                                                        L('USED',used[module.id], ModuleDef.valid(now,arch), ModuleDef.valid(next,arch))
                                                        device.nodes[module.id] = next
                                                        control.devices.modify(device)
                                                    }
                                                    return device.nodes[module.id]
                                                },
                                                remove () {
                                                    if (device.nodes[module.id]) {
                                                        // INDEX all chain-referenced modules
                                                        const chains = control.chains.get()
                                                        const usedMods = indexUnsplitMods(chains)
                                                        if (usedMods[module.id]) {
                                                            setAlert('Cannot delete: One or more chains reference this node')
                                                        }
                                                        else {
                                                            L('NODE-DELETE',module.id)
                                                            delete device.nodes[module.id]
                                                            device.nodeOrder = getNodeOrder(device).filter(id => id !== module.id)
                                                            control.devices.modify(device)
                                                        }
                                                    }
                                                },
                                                copy () {
                                                    const node = device.nodes[module.id]
                                                    // DERIVE unique name
                                                    if (node) {
                                                        // COLLECT current device node names
                                                        const names = Object
                                                            .values(DeviceDef.extractNodes(devices))
                                                            // CURRENT device
                                                            .filter(({device:{id}}) => id === device.id)
                                                            .map(({name}) => name)

                                                        // PRODUCE suitable *indexed* unique node name
                                                        const dupeName = Names.indexedName({name: node.name, names})
                                                        const copy = ModuleDef.copy({self:node,id:Identity.create(),name:dupeName.copy})
                                                        // RENAME origin node?
                                                        if (dupeName.origin) {
                                                            node.name = dupeName.origin
                                                        }
                                                        L('NODE-COPY',node.id,copy.id)
                                                        device.nodes[copy.id] = copy
                                                        device.nodeOrder = [...getNodeOrder(device).filter(id => id !== copy.id), copy.id]
                                                        control.devices.modify(device)
                                                    }
                                                },
                                                tags: control.devices.tags(module)
                                            }
                                        }}
                                    />
                                    )
                                })
                            }
                            <D 
                                k={['node-new','cta']}
                                onClick={() => setNewModal('mod')}>
                                <D k={["symbols",'add']}>add</D><D>NEW NODE</D>
                            </D>
                        </D>
                    </D>
                </>
                :
                <D k='empty'>No devices defined</D>
            }
        </D>
    )
}

export function DeviceNameSubpanel (
    {
        device,
        control,
        reset,
        name,mfr,prod,
        active,
    } : {
        device?: IDeviceDef
        reset?: boolean
        control : {
            all() : TRecDeviceDef
            edit?: {
                name (value?:string) : string|undefined
                prod (value?:string) : string|undefined
                mfr (value?:string)  : string|undefined
            }
            modify: {
                name (value?:string) : string|undefined
                prod (value?:string) : string|undefined
                mfr (value?:string)  : string|undefined
            }
            setAlert?: (prompt:string) => void
        }
        mfr?: string
        prod?: string
        name?: string
        active?: {
            mfr?: boolean
            prod?: boolean
            name?: boolean
            onActive?: (name:string) => void
        }
    }
)
{
    const ref = useRef<HTMLDivElement>(null)

    const {modify,edit,setAlert} = control

    // NEITHER manufacturer or product name defined
    const noName = !mfr?.length && !prod?.length

    const [nameErr,setNameErr] = useState<string|undefined>()

    // DEVICE NAMES, indexed
    const devNames = Object
        .values(control.all())
        // FILTER OUT current device
        .filter(({id}) => id !== device?.id)
        .reduce((p,{name}) => {
            p[name] = name
            return p
        },{})

    return (
        <D k='device'>
            <D k='field'>
                <D k='name'>Manufacturer</D>
                <D k='value'>
                    <EditableTextBox 
                        k='editbox'
                        value={mfr}
                        active={!!active?.mfr}
                        emptyText='enter manufacturer (optional)'
                        onFocus={() => active?.onActive && active.onActive('mfr')}
                        onEdit={value => {
                            edit?.mfr(value)
                            return value
                        }}
                        onSave={value => {
                            value = modify.mfr(value)
                            return value
                        }}
                    />
                </D>
            </D>
            <D k='field'>
                <D k='name'>Product</D>
                <D k='value'>
                    <EditableTextBox
                        k='editbox'
                        value={prod}
                        active={!!active?.prod}
                        onFocus={() => active?.onActive && active.onActive('prod')}
                        emptyText= 'enter product name (optional)'
                        onEdit={value => {
                            edit?.prod(value)
                            return value
                        }}
                        onSave={value => {
                            value = modify.prod(value)
                            return value
                        }}
                    />
                </D>
            </D>
            <D k='field name'>
                <D k='name'>Device Display Name</D>
                <D k={['value','naming']}>
                    <EditableTextBox
                        k={['editbox','name']}
                        emptyText='enter display name (required)'
                        onFocus={() => active?.onActive && active.onActive('name')}
                        active={!!active?.name}
                        value={name}
                        errTxt={nameErr}
                        onEdit={value => {
                            setNameErr(value && devNames[value] ? 'Node name is not unique' : undefined)
                            edit?.name(value)
                            return value
                        }}
                        onSave={value => {
                            setNameErr(undefined)
                            if (value) {
                                if (devNames[value]) {
                                    setAlert && setAlert('Device display name must be unique')
                                    return name
                                }
                            }
                            return modify.name(value)
                        }}
                    />
                    {reset && 
                        <D
                            k={['reset symbols',noName  && 'disabled']}
                            // RESET name
                            onClick={() => {
                                // NEITHER mfr or product name to work with
                                if (noName) return
                                const uniq = DeviceDef.getUniqueName({
                                        id:device?.id,
                                        mfr,
                                        prod,
                                        devices: control.all()
                                    }) || name
                                if (uniq) {
                                    modify.name(uniq)
                                }
                            }}
                        >restart_alt</D>
                    }
                </D>
            </D>
        </D>
    )
}

export function DeviceConfigPanel (
    {
        k,
        device,
        control,
        setAlert,
    } :
    {
        k: string,
        device: IDeviceDef
        control: IDevConfigController
        setAlert: (prompt:string) => void
    }
) {
    return (
        <D k={k}>
            <DeviceNameSubpanel
                reset={true}
                device={device}
                name={device.name}
                mfr={device.product.mfr}
                prod={device.product.name}
                control={{
                    all: control.all,
                    modify: {
                        name: name => {
                            if (!name) {
                                setAlert('A device display name is required')
                            }
                            else {
                                device.name = name
                                control.modify(device)
                            }
                            return device.name
                        },
                        prod: name => {
                            device.product.name = name
                            control.modify(device)
                            return device.product.name
                        },
                        mfr: name => {
                            device.product.mfr = name
                            control.modify(device)
                            return device.product.mfr
                        },
                    },
                }}

            />
            <D k='field'>
                <D k='name'>Device Info</D>
                <D k='value'>
                    <EditableTextBox
                        k={['value','notes']}
                        multiline={true}
                        value={device.notes}
                        emptyText='enter device notes'
                        onSave={value => {
                            device.notes = value
                            control.modify(device)
                            return value
                        }}
                    />
                </D>
            </D>
        </D>
    )
}
